import axios, { AxiosError } from "axios";
import getUserInfo from "api/login/getUserInfo";
import type {
  CreateSurveyRequest,
  UpdateSurveyRequest,
  SaveSurveyResultRequest,
  SetEditorsRequest,
  SetNotificationSettingsRequest,
  NotificationSettings,
  GetAllSurveysResponse,
  SurveyResultsResponse,
  SurveyEditor,
  SurveyEmployee,
  ApiErrorResponse,
  AiSummaryResponse,
  TeaserData,
} from "types/survey";

// Create a centralized axios instance with base URL logic
const createSurveyApiInstance = () => {
  const baseURL =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? "http://localhost:10000/api3.2/survey"
      : "/api3.2/survey";

  return axios.create({ baseURL });
};

// Create a noauth axios instance for public endpoints
const createNoAuthApiInstance = () => {
  const baseURL =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? "http://localhost:10000/api3.2/noauth"
      : "/api3.2/noauth";
  return axios.create({ baseURL });
};

// Get the survey API instance
const surveyApi = createSurveyApiInstance();

// Get the noauth API instance
const noAuthApi = createNoAuthApiInstance();

// Helper function to get X-Forwarded-Host header
const getForwardedHost = (apiUrl: string | undefined): string => {
  if (!apiUrl) {
    return window.location.host;
  }

  if (apiUrl.startsWith("/")) {
    return window.location.host;
  }

  try {
    const parsedUrl = new URL(apiUrl);
    return parsedUrl.host || window.location.host;
  } catch (error) {
    return window.location.host;
  }
};

// Helper function to get employee ID from user info
const getEmployeeId = async (): Promise<number | null> => {
  try {
    const userInfo = await getUserInfo();
    return userInfo.data.EmployeeId;
  } catch (error) {
    return null;
  }
};

// Helper function to unwrap API response and handle errors
const unwrapResponse = <T>(response: { data: { success: true; data: T } | ApiErrorResponse }): T => {
  const apiResponse = response.data;
  
  if (!apiResponse.success) {
    const error = new Error(apiResponse.error.message) as Error & { code?: string; details?: unknown };
    error.code = apiResponse.error.code;
    error.details = apiResponse.error.details;
    throw error;
  }

  return apiResponse.data;
};

// Helper function to download Excel file from base64 data
const downloadExcelFile = (fileName: string, mimeType: string, base64Data: string): void => {
  const binary = atob(base64Data);
  const len = binary.length;
  const u8 = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    u8[i] = binary.charCodeAt(i);
  }

  const blob = new Blob([u8], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// Create a new survey
export const createSurvey = async (
  surveyTitle: string,
  surveyContent: string
): Promise<{ surveyId: number; publicToken: string }> => {
  const employeeId = await getEmployeeId();
  if (employeeId === null) {
    throw new Error("Unable to fetch user information");
  }

  const payload: CreateSurveyRequest = {
    surveyTitle,
    surveyContent,
    employeeId,
  };

  try {
    const response = await surveyApi.post("/createSurvey", payload, {
      headers: {
        "Content-Type": "application/json",
        "X-Forwarded-Host": getForwardedHost(surveyApi.defaults.baseURL),
      },
    });

    return unwrapResponse<{ surveyId: number; publicToken: string }>(response);
  } catch (error) {
    console.error('[createSurvey] Failed:', error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      } else if (axiosError.response?.data) {
        // Handle cases where error structure is different
        const errorMessage = (axiosError.response.data as any).message ||
                            axiosError.message ||
                            'An error occurred while processing the request';
        throw new Error(errorMessage);
      }
    }
    throw error;
  }
};

// Update an existing survey
// Accepts either surveyId (integer) or token (UUID) to identify the survey
export const updateSurvey = async (
  identifier: { surveyId?: number; token?: string },
  surveyTitle: string,
  surveyContent: string
): Promise<void> => {
  const employeeId = await getEmployeeId();
  if (employeeId === null) {
    throw new Error("Unable to fetch user information");
  }

  const payload: UpdateSurveyRequest = {
    ...identifier,
    surveyTitle,
    surveyContent,
    employeeId,
  };

  try {
    const response = await surveyApi.put("/updateSurvey", payload, {
      headers: {
        "Content-Type": "application/json",
        "X-Forwarded-Host": getForwardedHost(surveyApi.defaults.baseURL),
      },
    });

    unwrapResponse<{ message: string }>(response);
  } catch (error) {
    console.error('[updateSurvey] Failed:', error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      } else if (axiosError.response?.data) {
        // Handle cases where error structure is different
        const errorMessage = (axiosError.response.data as any).message ||
                            axiosError.message ||
                            'An error occurred while processing the request';
        throw new Error(errorMessage);
      }
    }
    throw error;
  }
};

// Get a specific survey by ID (public endpoint, no auth required)
// Used by protected routes (CreateSurvey edit mode, PreviewSurvey)
export const getSurvey = async (surveyId: number): Promise<string> => {
  try {
    const response = await noAuthApi.get("/getSurvey", {
      params: { surveyId },
      headers: {
        "X-Forwarded-Host": getForwardedHost(noAuthApi.defaults.baseURL),
      },
    });

    const data = unwrapResponse<{ SurveyContent: string }>(response);
    return data.SurveyContent;
  } catch (error) {
    console.error('[getSurvey] Failed for surveyId:', surveyId, error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      } else if (axiosError.response?.data) {
        // Handle cases where error structure is different
        const errorMessage = (axiosError.response.data as any).message ||
                            axiosError.message ||
                            'An error occurred while processing the request';
        throw new Error(errorMessage);
      }
    }
    throw error;
  }
};

// Get a specific survey by public token (public endpoint, no auth required)
// Used by TakeSurvey for token-based URLs
export const getSurveyByToken = async (token: string): Promise<string> => {
  try {
    const response = await noAuthApi.get("/getSurvey", {
      params: { token },
      headers: {
        "X-Forwarded-Host": getForwardedHost(noAuthApi.defaults.baseURL),
      },
    });

    const data = unwrapResponse<{ SurveyContent: string }>(response);
    return data.SurveyContent;
  } catch (error) {
    console.error('[getSurveyByToken] Failed for token:', token, error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      } else if (axiosError.response?.data) {
        const errorMessage = (axiosError.response.data as any).message ||
                            axiosError.message ||
                            'An error occurred while processing the request';
        throw new Error(errorMessage);
      }
    }
    throw error;
  }
};

// Get all surveys
export const getAllSurveys = async (
  page: number = 0,
  pageSize: number = 25
): Promise<GetAllSurveysResponse> => {
  const employeeId = await getEmployeeId();
  if (employeeId === null) {
    throw new Error("Unable to fetch user information");
  }

  try {
    const response = await surveyApi.get("/getAllSurveys", {
      params: { employeeId, page, pageSize },
      headers: {
        "X-Forwarded-Host": getForwardedHost(surveyApi.defaults.baseURL),
      },
    });

    return unwrapResponse<GetAllSurveysResponse>(response);
  } catch (error) {
    console.error('[getAllSurveys] Failed:', error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      } else if (axiosError.response?.data) {
        // Handle cases where error structure is different
        const errorMessage = (axiosError.response.data as any).message ||
                            axiosError.message ||
                            'An error occurred while processing the request';
        throw new Error(errorMessage);
      }
    }
    throw error;
  }
};

// Delete a survey (creator only)
export const deleteSurvey = async (surveyId: number): Promise<void> => {
  const employeeId = await getEmployeeId();

  try {
    const response = await surveyApi.delete("/deleteSurvey", {
      params: { surveyId, employeeId },
      headers: {
        "X-Forwarded-Host": getForwardedHost(surveyApi.defaults.baseURL),
      },
    });

    unwrapResponse<{ message: string }>(response);
  } catch (error) {
    console.error('[deleteSurvey] Failed for surveyId:', surveyId, error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      } else if (axiosError.response?.data) {
        // Handle cases where error structure is different
        const errorMessage = (axiosError.response.data as any).message ||
                            axiosError.message ||
                            'An error occurred while processing the request';
        throw new Error(errorMessage);
      }
    }
    throw error;
  }
};

// Delete a single survey result (creator or editor only)
export const deleteSurveyResult = async (surveyResultId: number): Promise<void> => {
  const employeeId = await getEmployeeId();

  try {
    const response = await surveyApi.delete("/deleteSurveyResult", {
      params: { surveyResultId, employeeId },
      headers: {
        "X-Forwarded-Host": getForwardedHost(surveyApi.defaults.baseURL),
      },
    });

    unwrapResponse<{ message: string }>(response);
  } catch (error) {
    console.error('[deleteSurveyResult] Failed for surveyResultId:', surveyResultId, error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      } else if (axiosError.response?.data) {
        const errorMessage = (axiosError.response.data as any).message ||
                            axiosError.message ||
                            'An error occurred while processing the request';
        throw new Error(errorMessage);
      }
    }
    throw error;
  }
};

// Save survey result/response (public endpoint, no auth required)
// Accepts either { surveyId } or { token } to identify the survey
export const saveSurveyResult = async (
  identifier: { surveyId?: number; token?: string },
  resultContent: string,
  source?: string
): Promise<{ surveyResultId: number }> => {
  let employeeId = 0;
  try {
    const userInfo = await getUserInfo();
    employeeId = userInfo.data.EmployeeId;
  } catch (error) {
    // Use anonymous user (0) if unable to fetch user info
    employeeId = 0;
  }

  const payload: SaveSurveyResultRequest = {
    ...identifier,
    completeDate: new Date().toISOString(),
    resultContent,
    employeeId,
    ...(source && { source }),
  };

  try {
    const response = await noAuthApi.post("/saveSurveyResult", payload, {
      headers: {
        "Content-Type": "application/json",
        "X-Forwarded-Host": getForwardedHost(noAuthApi.defaults.baseURL),
      },
    });

    return unwrapResponse<{ surveyResultId: number }>(response);
  } catch (error) {
    console.error('[saveSurveyResult] Failed:', identifier, error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      } else if (axiosError.response?.data) {
        // Handle cases where error structure is different
        const errorMessage = (axiosError.response.data as any).message ||
                            axiosError.message ||
                            'An error occurred while processing the request';
        throw new Error(errorMessage);
      }
    }
    throw error;
  }
};

// Get survey completion count
export const getSurveyCompletionCount = async (
  surveyId: number
): Promise<{ surveyId: number; completionCount: number }> => {
  try {
    const response = await surveyApi.get("/getSurveyCompletionCount", {
      params: { surveyId },
      headers: {
        "X-Forwarded-Host": getForwardedHost(surveyApi.defaults.baseURL),
      },
    });

    return unwrapResponse<{ surveyId: number; completionCount: number }>(response);
  } catch (error) {
    console.error('[getSurveyCompletionCount] Failed for surveyId:', surveyId, error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      } else if (axiosError.response?.data) {
        // Handle cases where error structure is different
        const errorMessage = (axiosError.response.data as any).message ||
                            axiosError.message ||
                            'An error occurred while processing the request';
        throw new Error(errorMessage);
      }
    }
    throw error;
  }
};

// Get survey results as JSON
export const getSurveyResults = async (
  surveyId: number
): Promise<SurveyResultsResponse> => {
  try {
    const response = await surveyApi.get("/getSurveyResults", {
      params: { surveyId },
      headers: {
        "X-Forwarded-Host": getForwardedHost(surveyApi.defaults.baseURL),
      },
    });

    return unwrapResponse<SurveyResultsResponse>(response);
  } catch (error) {
    console.error('[getSurveyResults] Failed for surveyId:', surveyId, error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      } else if (axiosError.response?.data) {
        const errorMessage = (axiosError.response.data as any).message ||
                            axiosError.message ||
                            'An error occurred while processing the request';
        throw new Error(errorMessage);
      }
    }
    throw error;
  }
};

// Get editors for a survey
export const getEditors = async (surveyId: number): Promise<SurveyEditor[]> => {
  try {
    const response = await surveyApi.get("/getEditors", {
      params: { surveyId },
      headers: {
        "X-Forwarded-Host": getForwardedHost(surveyApi.defaults.baseURL),
      },
    });

    return unwrapResponse<SurveyEditor[]>(response);
  } catch (error) {
    console.error('[getEditors] Failed for surveyId:', surveyId, error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      }
    }
    throw error;
  }
};

// Set editors for a survey (full replacement, creator only)
export const setEditors = async (
  surveyId: number,
  editorEmployeeIds: number[]
): Promise<void> => {
  const employeeId = await getEmployeeId();
  if (employeeId === null) {
    throw new Error("Unable to fetch user information");
  }

  const payload: SetEditorsRequest = {
    surveyId,
    editorEmployeeIds,
    requestingEmployeeId: employeeId,
  };

  try {
    const response = await surveyApi.put("/setEditors", payload, {
      headers: {
        "Content-Type": "application/json",
        "X-Forwarded-Host": getForwardedHost(surveyApi.defaults.baseURL),
      },
    });

    unwrapResponse<{ message: string }>(response);
  } catch (error) {
    console.error('[setEditors] Failed for surveyId:', surveyId, error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      }
    }
    throw error;
  }
};

// Get notification settings for a survey (creator or editors)
export const getNotificationSettings = async (
  surveyId: number
): Promise<NotificationSettings> => {
  const employeeId = await getEmployeeId();
  if (employeeId === null) {
    throw new Error("Unable to fetch user information");
  }

  try {
    const response = await surveyApi.get("/getNotificationSettings", {
      params: { surveyId, employeeId },
      headers: {
        "X-Forwarded-Host": getForwardedHost(surveyApi.defaults.baseURL),
      },
    });

    return unwrapResponse<NotificationSettings>(response);
  } catch (error) {
    console.error('[getNotificationSettings] Failed for surveyId:', surveyId, error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      }
    }
    throw error;
  }
};

// Set notification settings for a survey (creator or editors)
export const setNotificationSettings = async (
  surveyId: number,
  enabled: boolean,
  emails: string[]
): Promise<void> => {
  const employeeId = await getEmployeeId();
  if (employeeId === null) {
    throw new Error("Unable to fetch user information");
  }

  const payload: SetNotificationSettingsRequest = {
    surveyId,
    enabled,
    emails,
    requestingEmployeeId: employeeId,
  };

  try {
    const response = await surveyApi.put("/setNotificationSettings", payload, {
      headers: {
        "Content-Type": "application/json",
        "X-Forwarded-Host": getForwardedHost(surveyApi.defaults.baseURL),
      },
    });

    unwrapResponse<{ message: string }>(response);
  } catch (error) {
    console.error('[setNotificationSettings] Failed for surveyId:', surveyId, error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      }
    }
    throw error;
  }
};

// Get all employees for the editor picker
export const getEmployees = async (): Promise<SurveyEmployee[]> => {
  try {
    const response = await surveyApi.get("/getEmployees", {
      headers: {
        "X-Forwarded-Host": getForwardedHost(surveyApi.defaults.baseURL),
      },
    });

    return unwrapResponse<SurveyEmployee[]>(response);
  } catch (error) {
    console.error('[getEmployees] Failed:', error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      }
    }
    throw error;
  }
};

// Generate or retrieve cached AI summary for survey results
export const getAiSummary = async (
  surveyId: number,
  force: boolean = false
): Promise<AiSummaryResponse> => {
  try {
    const response = await surveyApi.get("/getAiSummary", {
      params: { surveyId, ...(force && { force: "true" }) },
      headers: {
        "X-Forwarded-Host": getForwardedHost(surveyApi.defaults.baseURL),
      },
      timeout: 120000, // 2 minutes for AI generation
    });

    return unwrapResponse<AiSummaryResponse>(response);
  } catch (error) {
    console.error('[getAiSummary] Failed for surveyId:', surveyId, error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      }
    }
    throw error;
  }
};

/**
 * ONE-OFF: Generate AI teaser for the AI Readiness Survey.
 * Uses noauth endpoint since survey takers are not authenticated.
 * Returns null on any failure so the survey can proceed without interruption.
 * Remove when the AI Readiness campaign ends.
 */
export const generateTeaser = async (
  answers: Record<string, unknown>
): Promise<TeaserData | null> => {
  try {
    const response = await noAuthApi.post("/generateTeaser", { answers }, {
      headers: {
        "Content-Type": "application/json",
        "X-Forwarded-Host": getForwardedHost(noAuthApi.defaults.baseURL),
      },
      timeout: 30000,
    });
    const data = unwrapResponse<{ teaser: TeaserData | string | null }>(response);
    // Type guard: handle old backend returning a plain string or null
    if (data.teaser === null || typeof data.teaser === 'string') {
      return null;
    }
    return data.teaser;
  } catch (error) {
    console.error('[generateTeaser] Failed:', error);
    return null;
  }
};

// Download survey results as Excel
export const downloadSurveyResultsExcel = async (surveyId: number): Promise<void> => {
  try {
    const response = await surveyApi.get("/getSurveyResultsExcel", {
      params: { surveyId },
      headers: {
        "X-Forwarded-Host": getForwardedHost(surveyApi.defaults.baseURL),
      },
      responseType: "json",
    });

    const data = unwrapResponse<{
      fileName: string;
      mimeType: string;
      data: string;
    }>(response);

    downloadExcelFile(data.fileName, data.mimeType, data.data);
  } catch (error) {
    console.error('[downloadSurveyResultsExcel] Failed for surveyId:', surveyId, error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.error?.message) {
        throw new Error(axiosError.response.data.error.message);
      } else if (axiosError.response?.data) {
        // Handle cases where error structure is different
        const errorMessage = (axiosError.response.data as any).message ||
                            axiosError.message ||
                            'An error occurred while processing the request';
        throw new Error(errorMessage);
      }
    }
    throw error;
  }
};
